// The patient portal on the patient's record (docs/plans/phase3-portal-feedback-kiswahili.md
// §4, §7.2): status, enrolling with consent read aloud, linking children, suspending,
// resuming and closing, and asking for feedback on a recent visit by SMS.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatEatDate } from '@wodi/domain';
import type { PatientPortalView, PortalConsentTextView, SessionView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  RadioGroup,
  Section,
  Tag,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { patientKeys, patientVisits } from '../api/patients.ts';
import {
  changePortalStatus,
  endPortalChild,
  enrolInPortal,
  linkPortalChild,
  patientPortalQuery,
  portalConsentTextsQuery,
  portalStaffKeys,
  requestFeedback,
} from '../api/portal-staff.ts';
import { can } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';

const message = (error: unknown) =>
  error instanceof ApiError ? error.message : 'Something went wrong. Try again.';

const STATUS: Record<NonNullable<PatientPortalView['account']>['status'], string> = {
  active: 'Active',
  suspended: 'Suspended',
  closed: 'Closed',
};

function EnrolDialog({
  patientId,
  view,
  isOpen,
  onOpenChange,
}: {
  patientId: string;
  view: PatientPortalView;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const texts = useQuery({ ...portalConsentTextsQuery, enabled: isOpen });
  const [contactId, setContactId] = useState<string | null>(view.mobiles[0]?.contactId ?? null);
  const [textId, setTextId] = useState<string | null>(null);
  const enrol = useMutation({
    mutationFn: () =>
      enrolInPortal(patientId, { contactId: contactId ?? '', consentTextId: textId ?? '' }),
    onSuccess: (saved) => {
      queryClient.setQueryData(portalStaffKeys.patient(patientId), saved);
      onOpenChange(false);
      toasts.show({ title: 'Portal access given' });
    },
  });
  const chosen: PortalConsentTextView | undefined = texts.data?.find((t) => t.id === textId);
  return (
    <Dialog title="Give portal access" isOpen={isOpen} onOpenChange={onOpenChange}>
      {view.mobiles.length === 0 ? (
        <Notice tone="error">
          The patient has no mobile number of their own on file. Add one under Phones first.
        </Notice>
      ) : (
        <RadioGroup
          label="Send sign-in codes to"
          options={view.mobiles.map((m) => ({
            id: m.contactId,
            label: `Number ending ${m.phoneEnding}`,
          }))}
          value={contactId}
          onChange={setContactId}
        />
      )}
      {texts.isPending ? <LoadingBlock label="Loading the consent wording" rows={2} /> : null}
      {texts.isError ? <Notice tone="error">{message(texts.error)}</Notice> : null}
      {texts.data?.length === 0 ? (
        <Notice tone="error">
          No portal consent wording is set up. An administrator adds it in Settings &gt; Patient
          portal.
        </Notice>
      ) : null}
      {texts.data === undefined || texts.data.length === 0 ? null : (
        <RadioGroup
          label="Wording read to the patient"
          options={texts.data.map((t) => ({
            id: t.id,
            label: `${t.language === 'sw' ? 'Kiswahili' : 'English'}, version ${t.version}`,
          }))}
          value={textId}
          onChange={setTextId}
        />
      )}
      {chosen === undefined ? null : <Notice tone="info">{chosen.body}</Notice>}
      <p>Read the wording to the patient. Give access only if they agree.</p>
      {enrol.error === null ? null : <Notice tone="error">{message(enrol.error)}</Notice>}
      <DialogActions>
        <Button
          onPress={() => {
            onOpenChange(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={contactId === null || textId === null}
          isPending={enrol.isPending}
          onPress={() => {
            enrol.mutate();
          }}
        >
          The patient agrees: give access
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FeedbackRequest({ patientId }: { patientId: string }) {
  const online = useOnline();
  const visits = useQuery({
    queryKey: [...patientKeys.visits(patientId), 'feedback'],
    queryFn: () => patientVisits(patientId, undefined),
  });
  const ask = useMutation({
    mutationFn: requestFeedback,
    onSuccess: (result) => {
      toasts.show({ title: `Feedback link sent to the number ending ${result.phoneEnding}` });
    },
  });
  const [cutoff] = useState(() => Date.now() - 30 * 86_400_000);
  const visit = visits.data?.items.find(
    (v) =>
      v.status === 'closed' &&
      v.endedAt !== null &&
      new Date(v.endedAt).getTime() >= cutoff &&
      v.type !== 'mortuary',
  );
  if (visit === undefined) return null;
  return (
    <>
      {ask.error === null ? null : <Notice tone="error">{message(ask.error)}</Notice>}
      <div>
        <Button
          dense
          isDisabled={!online}
          isPending={ask.isPending}
          onPress={() => {
            ask.mutate(visit.id);
          }}
        >
          {`Ask for feedback by SMS on the visit of ${formatEatDate(new Date(visit.startedAt))}`}
        </Button>
      </div>
    </>
  );
}

export function PatientPortalSection({
  patientId,
  session,
}: {
  patientId: string;
  session: SessionView;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const portal = useQuery(patientPortalQuery(patientId));
  const [enrolling, setEnrolling] = useState(false);
  const [action, setAction] = useState<'suspend' | 'resume' | 'close' | null>(null);
  const [ending, setEnding] = useState<string | null>(null);
  const mayManage = can(session, 'portal.accounts.manage');
  const store = (saved: PatientPortalView) => {
    queryClient.setQueryData(portalStaffKeys.patient(patientId), saved);
  };
  const change = useMutation({
    mutationFn: (input: { action: 'suspend' | 'resume' | 'close'; reason: string }) =>
      changePortalStatus(patientId, input.action, input.reason),
    onSuccess: (saved) => {
      store(saved);
      setAction(null);
      toasts.show({ title: 'Portal access changed' });
    },
  });
  const link = useMutation({
    mutationFn: (relationshipId: string) => linkPortalChild(patientId, relationshipId),
    onSuccess: (saved) => {
      store(saved);
      toasts.show({ title: 'Child linked' });
    },
  });
  const end = useMutation({
    mutationFn: (input: { linkId: string; reason: string }) =>
      endPortalChild(patientId, input.linkId, input.reason),
    onSuccess: (saved) => {
      store(saved);
      setEnding(null);
      toasts.show({ title: 'Child link ended' });
    },
  });

  const view = portal.data;
  const account = view?.account ?? null;
  return (
    <Section title="Patient portal">
      {portal.isPending ? <LoadingBlock label="Loading portal access" rows={2} /> : null}
      {portal.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense isDisabled={!online} onPress={() => void portal.refetch()}>
              Try again
            </Button>
          }
        >
          {online
            ? message(portal.error)
            : "You're offline. Portal access loads once you reconnect."}
        </Notice>
      ) : null}
      {view === undefined ? null : !view.portalEnabled && account === null ? (
        <p>The patient portal is switched off for this organisation.</p>
      ) : account === null ? (
        <>
          <p>
            No portal access. The patient can see appointments, bills, results and allergies once
            they agree.
          </p>
          {mayManage ? (
            <div>
              <Button
                dense
                isDisabled={!online}
                onPress={() => {
                  setEnrolling(true);
                }}
              >
                Give portal access
              </Button>
            </div>
          ) : null}
          {mayManage && enrolling ? (
            <EnrolDialog
              patientId={patientId}
              view={view}
              isOpen={enrolling}
              onOpenChange={setEnrolling}
            />
          ) : null}
        </>
      ) : (
        <>
          <p>
            <Tag state={account.status === 'active' ? 'done' : 'neutral'}>
              {STATUS[account.status]}
            </Tag>{' '}
            {account.phoneEnding === null
              ? 'The mobile for codes is no longer on file.'
              : `Codes go to the number ending ${account.phoneEnding}.`}
          </p>
          {account.statusReason === null ? null : <p>{`Reason: ${account.statusReason}`}</p>}
          <p>
            {`Consent version ${account.consentTextVersion}, given ${formatEatDate(new Date(account.enrolledAt))}. `}
            {account.lastSignedInAt === null
              ? 'Not signed in yet.'
              : `Last signed in ${formatEatDate(new Date(account.lastSignedInAt))}.`}
          </p>
          {account.dependants.length === 0 ? null : (
            <ul>
              {account.dependants.map((d) => (
                <li key={d.linkId}>
                  {`${d.name} (${d.patientNumber})${d.visible ? '' : ': no longer shown (18 or over, relationship ended, or deceased)'} `}
                  {mayManage ? (
                    <Button
                      dense
                      variant="quiet"
                      onPress={() => {
                        setEnding(d.linkId);
                      }}
                    >
                      End link
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {link.error === null ? null : <Notice tone="error">{message(link.error)}</Notice>}
          {mayManage && account.status === 'active'
            ? view.linkable.map((c) => (
                <div key={c.relationshipId}>
                  <Button
                    dense
                    isDisabled={!online}
                    isPending={link.isPending && link.variables === c.relationshipId}
                    onPress={() => {
                      link.mutate(c.relationshipId);
                    }}
                  >
                    {`Let them see ${c.name}'s records`}
                  </Button>
                </div>
              ))
            : null}
          {mayManage ? (
            <div>
              {account.status === 'active' ? (
                <Button
                  dense
                  isDisabled={!online}
                  onPress={() => {
                    setAction('suspend');
                  }}
                >
                  Suspend access
                </Button>
              ) : (
                <Button
                  dense
                  isDisabled={!online}
                  onPress={() => {
                    setAction('resume');
                  }}
                >
                  Resume access
                </Button>
              )}{' '}
              <Button
                dense
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  setAction('close');
                }}
              >
                Close access
              </Button>
            </div>
          ) : null}
        </>
      )}
      {can(session, 'feedback.request.send') ? <FeedbackRequest patientId={patientId} /> : null}
      <ReasonDialog
        title={
          action === 'suspend'
            ? 'Suspend portal access'
            : action === 'resume'
              ? 'Resume portal access'
              : 'Close portal access'
        }
        isOpen={action !== null}
        onOpenChange={(open) => {
          if (!open) setAction(null);
        }}
        confirmLabel={
          action === 'close'
            ? 'Close access'
            : action === 'resume'
              ? 'Resume access'
              : 'Suspend access'
        }
        pendingLabel="Saving…"
        isPending={change.isPending}
        error={change.error === null ? null : message(change.error)}
        onConfirm={(reason) => {
          if (action !== null) change.mutate({ action, reason });
        }}
      >
        {action === 'close' ? (
          <p>
            Closing records that the patient withdrew consent for the portal. Their sessions end at
            once.
          </p>
        ) : null}
      </ReasonDialog>
      <ReasonDialog
        title="End the child's link"
        isOpen={ending !== null}
        onOpenChange={(open) => {
          if (!open) setEnding(null);
        }}
        confirmLabel="End link"
        pendingLabel="Saving…"
        isPending={end.isPending}
        error={end.error === null ? null : message(end.error)}
        onConfirm={(reason) => {
          if (ending !== null) end.mutate({ linkId: ending, reason });
        }}
      />
    </Section>
  );
}
